import { Injectable, Signal, signal } from '@angular/core';

/**
 * Dialog→corner FLIP handoff (async-clone-progress plan B1.10).
 *
 * The initiating dialog captures its progress-area `DOMRect` on EXPLICIT close
 * (user click or the 10 s minimize affordance — never a timer) and calls
 * [handoffFrom]; this service animates a lightweight ghost element from that
 * rect to the corner widget's current rect (transform + opacity only, driven by
 * the `--motion-slow` / `--ease-standard` tokens), then pulses [highlight] so
 * the widget briefly accents the row that just took over.
 *
 * Under `prefers-reduced-motion` the FLIP is skipped entirely — the ghost never
 * exists and only the highlight pulse fires (fade-level feedback).
 *
 * Tier-1: root signal service; the widget registers its wrapper element via
 * [registerWidget] so the ghost has a live target rect at handoff time.
 */
@Injectable({ providedIn: 'root' })
export class CloneHandoffService {
  private widgetEl: HTMLElement | null = null;

  private readonly _highlight = signal(0);
  /** Monotonic pulse counter — the widget reacts to changes with a brief accent. */
  readonly highlight: Signal<number> = this._highlight.asReadonly();

  /** The widget registers/unregisters its wrapper element (null on destroy/hide). */
  registerWidget(el: HTMLElement | null): void {
    this.widgetEl = el;
  }

  /** Animate the ghost from [sourceRect] to the widget corner, then pulse. */
  handoffFrom(sourceRect: DOMRect): void {
    const target = this.widgetEl?.getBoundingClientRect() ?? null;
    if (!target || target.width === 0 || this.prefersReducedMotion() || typeof document === 'undefined') {
      this.pulse();
      return;
    }

    const ghost = document.createElement('div');
    ghost.style.cssText = [
      'position: fixed',
      `left: ${sourceRect.left}px`,
      `top: ${sourceRect.top}px`,
      `width: ${sourceRect.width}px`,
      `height: ${sourceRect.height}px`,
      'border-radius: 12px',
      'background: var(--color-background-secondary, #0f172a)',
      'border: 1px solid rgba(243, 103, 23, 0.55)',
      'pointer-events: none',
      'z-index: 890',
      'will-change: transform, opacity',
    ].join(';');
    document.body.appendChild(ghost);

    const scaleX = target.width / Math.max(sourceRect.width, 1);
    const scaleY = target.height / Math.max(sourceRect.height, 1);
    const animation = ghost.animate(
      [
        { transform: 'translate(0, 0) scale(1, 1)', opacity: 0.9 },
        {
          transform:
            `translate(${target.left - sourceRect.left}px, ${target.top - sourceRect.top}px) ` +
            `scale(${scaleX}, ${scaleY})`,
          opacity: 0.15,
        },
      ],
      { duration: this.motionSlowMs(), easing: this.easeStandard() },
    );
    const cleanup = () => {
      ghost.remove();
      this.pulse();
    };
    animation.onfinish = cleanup;
    animation.oncancel = () => ghost.remove();
  }

  private pulse(): void {
    this._highlight.update((n) => n + 1);
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
      : true;
  }

  private motionSlowMs(): number {
    const raw = this.rootToken('--motion-slow');
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 360;
  }

  private easeStandard(): string {
    return this.rootToken('--ease-standard') || 'cubic-bezier(0.4, 0, 0.2, 1)';
  }

  private rootToken(name: string): string {
    if (typeof document === 'undefined') return '';
    try {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    } catch {
      return '';
    }
  }
}
